import type { TFunction } from 'i18next';
import type {
  MeasureMetadata,
  MeasureValuesOverTime,
} from '../../../../../api/work-periods-client/work-periods-client.type';
import {
  Measure,
  MeasureDataByEntity,
  MeasureUnits,
} from '../../../../../api/work-periods-client/work-periods-client.type';
import { downloadCSV } from '../../../../../helpers/csv-download';
import { getUnitsLabel } from '../../../process-analysis.helpers';
import type { MonthlyAveragesTableRow } from './table.type';

/**
 * Calculates the average of the values in the given data object, filtering out null values and optionally zero values.
 *
 * @param {MeasureValuesOverTime} data - The data object containing values over time
 * @param {boolean} isZeroValid - Whether zero values should be included in the calculation
 * @return {number | null} The average value of the data. Returns null if the data is empty or all values are null.
 */
const calculateAverage = (data: MeasureValuesOverTime, isZeroValid: boolean): number | null => {
  const validValues = Object.values(data).filter((value) => value !== null && (isZeroValid || value !== 0));

  if (validValues.length === 0) {
    return null;
  }

  return validValues.reduce((acc, value) => acc + value, 0) / validValues.length;
};

/**
 * Calculates the sum of the values in the given data object, filtering out null values.
 *
 * @param {MeasureValuesOverTime} data - The data object containing values over time
 * @return {number | null} The sum of the data. Returns null if the data is empty or all values are null.
 */
const calculateSum = (data: MeasureValuesOverTime): number | null => {
  const validValues = Object.values(data).filter((value) => value !== null && value !== undefined);

  if (validValues.length === 0) {
    return null;
  }

  return validValues.reduce((acc, value) => acc + value, 0);
};

/**
 * Transforms measure data into grouped table rows with averages or sums
 *
 * @param {MeasureDataByEntity | undefined} data - Raw measure data by entity
 * @param {string[]} entityIds - Array of entity IDs to include as columns
 * @param {Record<string, MeasureMetadata[]>} groupedMeasures - Measures grouped by category
 * @param {Measure[]} sumTypeMeasures - Measures that should be summed instead of averaged
 * @returns {Record<string, MonthlyAveragesTableRow[]>} Grouped table rows by measure group
 */
const transformMonthlyAveragesData = (
  data: MeasureDataByEntity | undefined,
  entityIds: string[],
  groupedMeasures: Record<string, MeasureMetadata[]>,
  sumTypeMeasures: Measure[] = [],
): Record<string, MonthlyAveragesTableRow[]> => {
  if (!data || Object.keys(data).length === 0) {
    return {};
  }

  const groupedRows: Record<string, MonthlyAveragesTableRow[]> = {};

  Object.entries(groupedMeasures).forEach(([groupName, measures]) => {
    const rows: MonthlyAveragesTableRow[] = [];

    measures.forEach((measure) => {
      const isSumType = sumTypeMeasures.includes(measure.measure_name as Measure);

      const row: MonthlyAveragesTableRow = {
        measureName: measure.measure_name,
        measureTitle: measure.measure_title,
        measureDescription: measure.measure_description || '',
        measureUnits: measure.measure_units,
        isZeroValid: measure.is_zero_valid,
        isSumType,
      };

      entityIds.forEach((entityId) => {
        const entityData = data[entityId];
        const measureData = entityData?.[measure.measure_name as Measure];

        if (measureData) {
          let value = isSumType ? calculateSum(measureData) : calculateAverage(measureData, measure.is_zero_valid);

          if (measure.measure_units === MeasureUnits.Hours && typeof value === 'number') {
            value /= 24;
          }

          row[entityId] = value;
        } else {
          row[entityId] = null;
        }
      });

      rows.push(row);
    });

    groupedRows[groupName] = rows;
  });

  return groupedRows;
};

/**
 * Exports monthly averages data as CSV
 *
 * @param {Record<string, MonthlyAveragesTableRow[]>} transformedData - Grouped table rows by measure group
 * @param {string[]} entityIds - Array of entity IDs to include as columns
 * @param {Record<string, string>} entityNames - Mapping of entity IDs to display names
 * @param {TFunction} t - Translation function from `useTranslation()`
 * @return {void}
 */
const exportMonthlyAveragesCSV = (
  transformedData: Record<string, MonthlyAveragesTableRow[]>,
  entityIds: string[],
  entityNames: Record<string, string>,
  t: TFunction,
): void => {
  try {
    const csvData: (string | number)[][] = [];

    // Add header row
    const headerRow = [t('common.measures'), ...entityIds.map((id) => entityNames[id] || id)];
    csvData.push(headerRow);

    // Add data rows grouped by measure group
    Object.entries(transformedData).forEach(([groupName, rows]) => {
      // Add group header
      csvData.push([groupName, ...entityIds.map(() => '')]);

      // Add measure rows
      rows.forEach((row) => {
        const dataRow = [
          row.measureTitle,
          ...entityIds.map((entityId) => {
            const value = row[entityId];
            if (value === null || value === undefined) return '-';

            if (typeof value === 'number') {
              const formattedValue = row.measureUnits === MeasureUnits.Percentage ? value.toFixed(1) : value.toFixed(2);
              return `${formattedValue} ${getUnitsLabel(row.measureUnits as MeasureUnits, t)}`;
            }

            return String(value);
          }),
        ];
        csvData.push(dataRow);
      });

      // Add empty row between groups
      csvData.push(['', ...entityIds.map(() => '')]);
    });

    downloadCSV(csvData, 'monthly-averages.csv');
  } catch (error) {
    console.error('Error generating CSV data:', error);
  }
};

export { exportMonthlyAveragesCSV, getUnitsLabel, transformMonthlyAveragesData };
